import { GAINS_EXEMPTION_ASSUMPTION } from '@/domain/rollup'
import type { Measure } from '~/state/queries'

export interface FigureChoice {
  key: Measure
  label: string
  /**
   * Absent for a figure every household can read; named for one that answers
   * nothing until the household is shaped that way.
   */
  gate?: keyof HouseholdShape
}

/** The two facts about a household that decide which figures say anything. */
export interface HouseholdShape {
  /** Something is held through an entity other than the household itself. */
  company: boolean
  /** Something held cannot be sold. */
  unsellable: boolean
}

/**
 * The three ways of counting the same holdings, as a page offers them to be
 * read in. Performance, Risk and Units each kept their own copy of this list;
 * a figure that some households cannot be offered is not a thing to say three
 * times.
 */
export const FIGURES: readonly FigureChoice[] = [
  { key: 'lookThrough', label: 'Look-through' },
  { key: 'spendable', label: 'Spendable', gate: 'company' },
  { key: 'liquid', label: 'Liquid', gate: 'unsellable' },
]

/**
 * The figures a household can be offered.
 *
 * Each of the two later figures takes something away from the one before it:
 * Spendable the tax on getting a company's money out, Liquid what cannot be
 * sold. A household with nothing for one of them to take away is offered the
 * same number under two names, so that figure is left out (PRO-237 for
 * Spendable, PRO-267 for Liquid). Both are decided by what the household holds,
 * not by whether two totals agree on the day: one unsellable holding makes
 * Liquid a genuinely different answer, and it stays offered however little that
 * holding happens to be worth this month.
 */
export function figuresFor(shape: HouseholdShape): readonly FigureChoice[] {
  return FIGURES.filter((figure) => figure.gate === undefined || shape[figure.gate])
}

/**
 * The figure a page should actually read, given what it is offering.
 *
 * A figure can stop being offered while it is the one chosen — a company
 * disposed of between two refetches, or a choice restored from somewhere other
 * than the component's own state. A control whose value names a choice that has
 * left its list renders with nothing selected, above a figure that no longer
 * matches it: a wrong label on a right number, which is worse than the repeated
 * figure this gate exists to remove.
 */
export function shownFigure(figures: readonly FigureChoice[], chosen: Measure): Measure {
  if (figures.some((figure) => figure.key === chosen)) return chosen
  return figures[0]?.key ?? 'lookThrough'
}

/**
 * What Spendable assumed, for a page that offers it as one figure among three.
 *
 * The Dashboard, the hero chart's measure note and the tax card each say this
 * where they show the figure; Performance, Risk and Units offered it through a
 * control that carries a label and nothing else, so a household reading a
 * Danish tax rule's output had nowhere to learn that from where it stood. Said
 * here rather than inside the control: a picker that grows a slot for prose
 * would grow one for every picker in the product.
 *
 * The rule's half of the sentence comes from the exemption itself, so a kind
 * added to or taken out of the exempt set moves this too — a disclosure that
 * goes on describing a rule it no longer applies is worse than none.
 *
 * Keyed on the figure on screen rather than on the household's shape. Only a
 * household offered Spendable can be reading it, so a household that is never
 * shown the figure is never shown the sentence either; and a company household
 * reading Look-through is not owed an explanation of a figure it is not
 * looking at.
 */
export function spendableAssumption(shown: Measure): string | null {
  if (shown !== 'spendable') return null
  return `Spendable is after the tax it would cost to get the company's money out. ${GAINS_EXEMPTION_ASSUMPTION}`
}
